/* OneVine rosary page: the renderer.
   Raw WebGL 1, one program. Everything is a flat quad shaded in the fragment
   shader: the hand drawing, each bead (a sphere worked out per pixel), each
   chain link, the medal, the crucifix and the round window of a mystery. The
   rosary itself never moves; only the camera (position, zoom, turn) does.
   The controller is assets/onevine-rosary.js. */
(function () {
  'use strict';

  /* Measured from the hand drawing by tools/prepare-hand.cjs, as fractions of
     its width: the three drawn beads, the patch of the bead mask and the
     close-up tile. Keep in step with that script's report. */
  var ART = {
    beads: [
      { x: 0.81579, y: 0.17265, r: 0.03868 },
      { x: 0.85008, y: 0.32695, r: 0.03828 },
      { x: 0.87719, y: 0.46212, r: 0.03868 }
    ],
    mask: { x: 0.79266, y: 0.26954, size: 0.11483 },
    tile: { x: 0.5715, y: 0.11271, size: 0.4285 }
  };

  var VERTEX = [
    'attribute vec2 aCentre;',
    'attribute vec2 aCorner;',
    'attribute vec4 aShape;',
    'attribute vec4 aParam;',
    'uniform vec2 uCam;',
    'uniform float uZoom;',
    'uniform vec2 uView;',
    'uniform float uRot;',
    'varying vec2 vCorner;',
    'varying vec4 vParam;',
    'varying float vKind;',
    'varying vec2 vHalf;',
    'varying vec2 vWorld;',
    'varying vec2 vAxis;',
    'varying vec2 vCentre;',
    'void main(){',
    '  float c=cos(aShape.z), s=sin(aShape.z);',
    '  vec2 l=aCorner*aShape.xy;',
    '  vec2 w=aCentre+vec2(l.x*c-l.y*s, l.x*s+l.y*c);',
    '  vec2 d=(w-uCam)*uZoom;',
    '  float cr=cos(uRot), sr=sin(uRot);',
    '  d=vec2(d.x*cr-d.y*sr, d.x*sr+d.y*cr);',
    '  gl_Position=vec4(d.x/(uView.x*.5), -d.y/(uView.y*.5), 0., 1.);',
    '  vCorner=aCorner; vParam=aParam; vKind=aShape.w; vHalf=aShape.xy*uZoom; vWorld=w; vCentre=aCentre;',
    '  float a=aShape.z+uRot; vAxis=vec2(cos(a), sin(a));',
    '}'
  ].join('\n');

  var FRAGMENT = [
    '#ifdef GL_FRAGMENT_PRECISION_HIGH',
    'precision highp float;',
    '#else',
    'precision mediump float;',
    '#endif',
    'varying vec2 vCorner;',
    'varying vec4 vParam;',
    'varying float vKind;',
    'varying vec2 vHalf;',
    'varying vec2 vWorld;',
    'varying vec2 vAxis;',
    'varying vec2 vCentre;',
    'uniform sampler2D uMask;',
    'uniform vec4 uMaskRect;',
    'uniform sampler2D uArt0;',
    'uniform sampler2D uArt1;',
    'uniform float uArtSlot;',
    'uniform sampler2D uHand;',
    'uniform sampler2D uTile;',
    'uniform vec4 uHandFx;',   // blur (mip bias), brightness, opacity, how much of the close-up tile shows
    'uniform vec3 uTint;',
    'uniform vec2 uFocus;',
    'uniform vec4 uFade;',     // soften from, soften to, vanish from, vanish to (distance from the focus)
    'uniform float uLit;',     // how far the pinched bead has been prayed, 0 to 1
    'const vec3 KEY=vec3(-.42,.60,.68);',
    'vec2 toScreen(vec2 g){ return vec2(g.x*vAxis.x-g.y*vAxis.y, g.x*vAxis.y+g.y*vAxis.x); }',
    'vec3 finish(vec3 c){ c=c/(1.+c*.22); return pow(max(c,0.), vec3(1./2.2)); }',
    'vec3 gold(vec3 n, float occ){',
    '  vec3 L=normalize(KEY);',
    '  float diff=clamp(dot(n,L)*.5+.5,0.,1.);',
    '  vec3 col=mix(vec3(.10,.045,.006), vec3(.86,.46,.07), diff*diff);',
    '  vec3 H=normalize(L+vec3(0.,0.,1.));',
    '  float nh=max(dot(n,H),0.);',
    '  col+=vec3(1.,.9,.62)*pow(nh,36.)*1.5;',
    '  col+=vec3(1.,.74,.3)*smoothstep(.5,.98,n.y)*.2;',
    '  col+=vec3(.5,.2,.03)*smoothstep(.4,.95,-n.y)*.12;',
    '  return col*occ;',
    '}',
    'float sdCapsule(vec2 q, float a, float r){ q.y=abs(q.y)-a; q.y=max(q.y,0.); return length(q)-r; }',
    'vec2 gradCapsule(vec2 q, float a){ float y=max(abs(q.y)-a,0.)*sign(q.y); vec2 g=vec2(q.x,y); float l=length(g); return l>1e-4? g/l : vec2(0.,1.); }',
    'float sdBox(vec2 q, vec2 b, float r){ vec2 d=abs(q)-b+r; return length(max(d,0.))+min(max(d.x,d.y),0.)-r; }',
    'float smin(float a, float b, float k){ float h=clamp(.5+.5*(b-a)/k,0.,1.); return mix(b,a,h)-k*h*(1.-h); }',
    'float limb(vec2 q, vec2 a, vec2 b, float r){ vec2 pa=q-a, ba=b-a; float h=clamp(dot(pa,ba)/dot(ba,ba),0.,1.); return length(pa-ba*h)-r; }',
    // The figure of Christ on the cross, in units of the cross's half width, measured from the middle of the crossbar.
    'float corpus(vec2 q){',
    '  float d=length(q-vec2(-.035,.02))-.118;',                       // head, bowed to one side
    '  d=smin(d, limb(q,vec2(0.,.17),vec2(0.,.57),.108), .07);',       // chest to waist
    '  d=smin(d, limb(q,vec2(-.11,.17),vec2(-.77,-.03),.043), .05);',  // arms, raised to the nails
    '  d=smin(d, limb(q,vec2(.11,.17),vec2(.77,-.03),.043), .05);',
    '  d=smin(d, sdBox(q-vec2(0.,.70),vec2(.13,.078),.05), .04);',     // loincloth
    '  d=smin(d, limb(q,vec2(-.02,.79),vec2(.055,1.05),.07), .04);',   // thighs, knees turned
    '  d=smin(d, limb(q,vec2(.055,1.05),vec2(0.,1.33),.056), .04);',   // shins and feet
    '  return d;',
    '}',
    'void main(){',
    '  vec2 p=vCorner;',
    '  if(vKind>8.5){',                                  // 9: the hand drawing, 10: its close-up tile
    '    vec2 uv=p*.5+.5;',
    '    if(vKind<9.5){ vec4 t=texture2D(uHand,uv,uHandFx.x); gl_FragColor=vec4(t.rgb*uHandFx.y,t.a)*uHandFx.z; }',
    '    else { vec4 t=texture2D(uTile,uv); gl_FragColor=vec4(t.rgb*uHandFx.y,t.a)*(uHandFx.z*uHandFx.w); }',
    '    return;',
    '  }',
    '  float px=1./max(vHalf.x,1.);',
    '  vec3 col=vec3(0.); float alpha=0.;',
    '  float away=distance(vCentre,uFocus);',
    '  float blur=smoothstep(uFade.x,uFade.y,away);',
    '  float gone=1.-smoothstep(uFade.z,uFade.w,away);',
    '  if(gone<=0.) discard;',
    '  if(vKind<.5){',                                   // 0: a Hail Mary bead, blue glass
    '    p*=1.06; float d=length(p);',
    '    alpha=1.-smoothstep(1.-mix(1.6*px,.7,blur), 1.+.4*px, d);',
    '    if(alpha<=0.) discard;',
    '    float z=sqrt(max(0.,1.-min(d*d,1.)));',
    '    vec2 s=toScreen(p);',
    '    vec3 n=vec3(s.x,-s.y,z);',
    '    vec3 L=normalize(KEY);',
    '    float diff=clamp(dot(n,L)*.62+.38,0.,1.); diff*=diff;',
    '    vec3 deep=vec3(.004,.012,.11), base=vec3(.016,.07,.62);',
    '    col=mix(deep,base,diff);',
    '    col+=vec3(.02,.06,.32)*pow(max(n.y,0.),2.)*.5;',
    '    col+=uTint*pow(max(-n.y,0.),2.5)*(1.-z)*.55;',
    '    float fres=pow(1.-z,3.2);',
    '    col=mix(col,deep*.5,fres*.8);',
    '    col+=vec3(.05,.12,.5)*pow(1.-z,7.)*.9;',
    '    vec3 H=normalize(L+vec3(0.,0.,1.)); float nh=max(dot(n,H),0.);',
    '    float glint=pow(nh,150.)*2.6+pow(nh,18.)*.14;',
    '    vec3 L2=normalize(vec3(.62,-.55,.55)); float nh2=max(dot(n,normalize(L2+vec3(0.,0.,1.))),0.);',
    '    float f=vParam.z>.5? uLit : 0.;',               //   prayed: an ember grows until the bead is gold
    '    if(f>.001){',
    '      float core=exp(-d*d*mix(6.5,.28,f));',
    '      float m=clamp(core*(.3+f*1.25),0.,1.)*smoothstep(0.,.2,f);',
    '      vec3 lit=mix(vec3(.82,.3,.02), vec3(1.5,.92,.3), core*core)*(.7+.45*diff);',
    '      lit=mix(lit, vec3(.42,.14,.01), fres*.6);',
    '      col=mix(col,lit,m);',
    '    }',
    '    col+=(vec3(1.)*glint+vec3(.3,.4,.9)*pow(nh2,26.)*.12)*(1.-blur);',
    '    col+=vec3(.04,.13,.62)*pow(max(dot(n,normalize(vec3(.35,-.72,.42))),0.),4.)*.5*(1.-fres)*(1.-f);',
    // The thumb is in front of this bead. The mask stops at the bead's own edge, so the rim also asks a
    // little further in; without that the bead's outline is drawn as a thin line across the thumb.
    '    if(vParam.z>.5){',
    '      vec2 off=vWorld-vCentre;',
    '      float cut=texture2D(uMask,(vWorld-uMaskRect.xy)/uMaskRect.z).r;',
    '      cut=min(cut,texture2D(uMask,(vCentre+off*.93-uMaskRect.xy)/uMaskRect.z).r);',
    '      cut=min(cut,texture2D(uMask,(vCentre+off*.86-uMaskRect.xy)/uMaskRect.z).r);',
    '      alpha*=mix(1.,cut,uMaskRect.w);',
    '    }',
    '    col=finish(col);',
    '  } else if(vKind<1.5){',                           // 1: a chain link seen face-on
    '    vec2 q=p*vHalf; float R=vHalf.x*.66, T=vHalf.x*.3, a=vHalf.y-vHalf.x;',
    '    float sd=sdCapsule(q,a,R); float ring=abs(sd)-T;',
    '    alpha=(1.-smoothstep(-.7-blur*3.,.7,ring))*(1.-blur*.55); if(alpha<=0.) discard;',
    '    float t=clamp(sd/T,-1.,1.); vec2 g=toScreen(gradCapsule(q,a));',
    '    vec3 n=normalize(vec3(g.x*t,-g.y*t,sqrt(max(0.,1.-t*t))+.05));',
    '    col=finish(gold(n,1.));',
    '  } else if(vKind<2.5){',                           // 2: a chain link seen edge-on
    '    vec2 q=p*vHalf; float w=vHalf.x*.42, a=vHalf.y-w;',
    '    float sd=sdCapsule(q,a,w);',
    '    alpha=(1.-smoothstep(-.7-blur*3.,.7,sd))*(1.-blur*.55); if(alpha<=0.) discard;',
    '    float t=clamp(q.x/w,-1.,1.); vec2 g=toScreen(vec2(1.,0.));',
    '    vec3 n=normalize(vec3(g.x*t,-g.y*t,sqrt(max(0.,1.-t*t))+.05));',
    '    col=finish(gold(n,.92));',
    '  } else if(vKind<3.5){',                           // 3: a glow, added to whatever is behind it
    '    float d=length(p); float g=pow(max(0.,1.-d),2.4)*vParam.x;',
    '    gl_FragColor=vec4(vParam.yzw*g,0.); return;',
    '  } else if(vKind<4.5){',                           // 4: an Our Father bead, gold
    '    p*=1.06; float d=length(p);',
    '    alpha=1.-smoothstep(1.-mix(1.6*px,.7,blur), 1.+.4*px, d); if(alpha<=0.) discard;',
    '    float z=sqrt(max(0.,1.-min(d*d,1.))); vec2 s=toScreen(p);',
    '    vec3 n=vec3(s.x,-s.y,z);',
    '    col=finish(gold(n,1.-pow(1.-z,4.)*.5));',
    '  } else if(vKind<5.5){',                           // 5: the centre medal
    '    p*=1.04; float d=length(p);',
    '    alpha=(1.-smoothstep(1.-1.6*px, 1.+.4*px, d))*(1.-blur*.5); if(alpha<=0.) discard;',
    '    vec2 s=toScreen(p);',
    '    float rim=smoothstep(.74,.8,d);',
    '    float tr=clamp((d-.88)/.12,-1.,1.);',
    '    vec2 dir=d>1e-4? s/d : vec2(0.,1.);',
    '    vec3 nr=normalize(vec3(dir.x*tr,-dir.y*tr,sqrt(max(0.,1.-tr*tr))+.1));',
    '    vec3 nd=normalize(vec3(-s.x*.35,s.y*.35,1.));',
    '    vec3 enamel=mix(vec3(.004,.015,.13),vec3(.012,.06,.5),clamp(dot(nd,normalize(KEY))*.6+.5,0.,1.));',
    '    float cross=min(sdBox(p,vec2(.07,.34),.03), sdBox(p-vec2(0.,-.1),vec2(.22,.07),.03));',
    '    float cm=1.-smoothstep(-.02,.02,cross);',
    '    vec3 inner=mix(finish(enamel), finish(gold(normalize(vec3(-s.x*.2,s.y*.2+.2,1.)),1.)), cm);',
    '    col=mix(inner, finish(gold(nr,1.)), rim);',
    '  } else if(vKind<6.5){',                           // 6: a mystery, a round window in a gold frame
    '    p*=1.03; float d=length(p);',
    '    alpha=1.-smoothstep(1.-1.6*px, 1.+.4*px, d); if(alpha<=0.) discard;',
    '    vec2 s=toScreen(p);',
    '    float rim=smoothstep(.865,.885,d);',
    '    float tr=clamp((d-.94)/.06,-1.,1.);',
    '    vec2 dir=d>1e-4? s/d : vec2(0.,1.);',
    '    vec3 nr=normalize(vec3(dir.x*tr,-dir.y*tr,sqrt(max(0.,1.-tr*tr))+.1));',
    '    vec2 uv=vec2(.5)+vParam.yz+(p/.875*.5)*vParam.w;',
    '    vec3 art=uArtSlot<.5? texture2D(uArt0,uv).rgb : texture2D(uArt1,uv).rgb;',
    '    art*=1.-smoothstep(.55,.875,d)*.42;',            //   the frame shades the picture
    '    art+=vec3(1.,.95,.85)*pow(max(0.,1.-length(p-vec2(-.3,-.42))*1.7),3.)*.07;',
    '    vec3 blank=finish(mix(vec3(.004,.015,.13),vec3(.012,.05,.4),clamp(.6-p.y*.5,0.,1.)));',
    '    col=mix(mix(blank,art,vParam.x), finish(gold(nr,1.)), rim);',
    '  } else {',                                        // 7: the crucifix: a cross, the figure on it, and the title above
    '    vec2 q=p*vHalf; float u=vHalf.x;',
    '    float bw=u*.2; float bar=-vHalf.y*.36;',
    '    float inV=sdBox(q,vec2(bw,vHalf.y*.96),bw*.45), inH=sdBox(q-vec2(0.,bar),vec2(u*.94,bw),bw*.45);',
    '    float sd=min(inV,inH);',
    '    alpha=(1.-smoothstep(-.7,.7,sd))*(1.-blur*.5); if(alpha<=0.) discard;',
    '    float bev=clamp(-sd/(bw*.8),0.,1.);',
    '    vec2 gl=inV<inH? vec2(sign(q.x),0.) : vec2(0.,sign(q.y-bar));',
    '    vec2 g=toScreen(gl)*(1.-bev);',
    '    vec3 n=normalize(vec3(g.x,-g.y,.35+bev));',
    '    col=gold(n,.9+.1*bev);',
    '    vec2 k=(q-vec2(0.,bar))/u;',
    '    float cd=corpus(k);',
    '    col*=1.-.3*(1.-smoothstep(0.,.05,cd));',         //   the figure's shadow on the wood
    '    float plate=sdBox(k-vec2(0.,-.62),vec2(.17,.07),.03);',
    '    col=mix(col, col*1.3+vec3(.05,.04,.02), 1.-smoothstep(-.012,.012,plate));',
    '    if(cd<.02){',
    '      float e=.014;',
    '      vec2 slope=vec2(corpus(k+vec2(e,0.))-cd, corpus(k+vec2(0.,e))-cd)/e;',
    '      float depth=clamp(-cd/.085,0.,1.);',
    '      vec2 gs=toScreen(slope)*(1.-depth*depth);',
    '      vec3 nc=normalize(vec3(gs.x,-gs.y,.4+depth*.8));',
    '      vec3 figure=gold(nc,1.)*vec3(.9,1.04,1.22)+vec3(.2,.2,.19);',   //   silver against the gold of the cross, as on many rosaries
    '      col=mix(col, figure, 1.-smoothstep(-.02,.012,cd));',
    '    }',
    '    col=finish(col);',
    '  }',
    '  alpha*=gone;',
    '  gl_FragColor=vec4(col*alpha,alpha);',
    '}'
  ].join('\n');

  var CORNERS = [-1, -1, 1, -1, 1, 1, -1, -1, 1, 1, -1, 1];
  var STRIDE = 12;
  var UNIT = { mask: 0, art0: 1, hand: 2, tile: 3, art1: 4 };

  function compile(gl, type, source) {
    var shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    return gl.getShaderParameter(shader, gl.COMPILE_STATUS) ? shader : null;
  }

  function fill(data, offset, item) {
    var p = item.p;
    for (var v = 0; v < 6; v++) {
      data[offset++] = item.x; data[offset++] = item.y;
      data[offset++] = CORNERS[v * 2]; data[offset++] = CORNERS[v * 2 + 1];
      data[offset++] = item.hw; data[offset++] = item.hh; data[offset++] = item.rot || 0; data[offset++] = item.kind;
      data[offset++] = p ? p[0] : 0; data[offset++] = p ? p[1] : 0; data[offset++] = p ? p[2] : 0; data[offset++] = p ? p[3] : 0;
    }
    return offset;
  }

  // Returns null when WebGL is missing or a shader will not build.
  function createRenderer(canvas) {
    var gl = null;
    try { gl = canvas.getContext('webgl', { alpha: true, antialias: false, premultipliedAlpha: true, depth: false, stencil: false, powerPreference: 'high-performance' }); } catch (error) { gl = null; }
    if (!gl) return null;
    var vertex = compile(gl, gl.VERTEX_SHADER, VERTEX);
    var fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
    if (!vertex || !fragment) return null;
    var program = gl.createProgram();
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
    gl.useProgram(program);

    var uniform = {};
    ['uCam', 'uZoom', 'uView', 'uRot', 'uMask', 'uMaskRect', 'uArt0', 'uArt1', 'uArtSlot', 'uHand', 'uTile', 'uHandFx', 'uTint', 'uFocus', 'uFade', 'uLit'].forEach(function (name) { uniform[name] = gl.getUniformLocation(program, name); });
    var attribute = {};
    ['aCentre', 'aCorner', 'aShape', 'aParam'].forEach(function (name) {
      attribute[name] = gl.getAttribLocation(program, name);
      gl.enableVertexAttribArray(attribute[name]);
    });
    function point(buffer) {
      var bytes = STRIDE * 4;
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.vertexAttribPointer(attribute.aCentre, 2, gl.FLOAT, false, bytes, 0);
      gl.vertexAttribPointer(attribute.aCorner, 2, gl.FLOAT, false, bytes, 8);
      gl.vertexAttribPointer(attribute.aShape, 4, gl.FLOAT, false, bytes, 16);
      gl.vertexAttribPointer(attribute.aParam, 4, gl.FLOAT, false, bytes, 32);
    }

    var textures = {};
    var ready = { hand: false, tile: false };
    function texture(name, source, options) {
      options = options || {};
      if (!textures[name]) textures[name] = gl.createTexture();
      gl.activeTexture(gl.TEXTURE0 + UNIT[name]);
      gl.bindTexture(gl.TEXTURE_2D, textures[name]);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, !!options.premultiply);
      if (source) gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
      else gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 0]));
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      if (source && options.mipmap) {
        gl.generateMipmap(gl.TEXTURE_2D);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      } else {
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      }
      if (name in ready) ready[name] = !!source;
    }
    Object.keys(UNIT).forEach(function (name) { texture(name, null); });
    gl.uniform1i(uniform.uMask, UNIT.mask);
    gl.uniform1i(uniform.uArt0, UNIT.art0);
    gl.uniform1i(uniform.uArt1, UNIT.art1);
    gl.uniform1i(uniform.uHand, UNIT.hand);
    gl.uniform1i(uniform.uTile, UNIT.tile);
    gl.uniform4f(uniform.uMaskRect, ART.mask.x, ART.mask.y, ART.mask.size, 0);

    var fixed = gl.createBuffer();
    var moving = gl.createBuffer();
    var counts = { hand: 0, rest: 0 };
    var scratch = new Float32Array(8 * 6 * STRIDE);
    gl.bindBuffer(gl.ARRAY_BUFFER, moving);
    gl.bufferData(gl.ARRAY_BUFFER, scratch, gl.DYNAMIC_DRAW);

    function drawMoving(items) {
      if (!items || !items.length) return;
      var offset = 0;
      var n = Math.min(items.length, 8);
      for (var i = 0; i < n; i++) offset = fill(scratch, offset, items[i]);
      point(moving);
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, scratch);
      gl.drawArrays(gl.TRIANGLES, 0, n * 6);
    }

    return {
      lost: function () { return gl.isContextLost(); },
      // The hand and its close-up are premultiplied and mipmapped; a painting is neither.
      setHand: function (image) { texture('hand', image, { premultiply: true, mipmap: true }); },
      setTile: function (image) { texture('tile', image, { premultiply: true, mipmap: true }); },
      // Two paintings are held at once: slot 0 or 1.
      setArt: function (slot, image) { texture(slot ? 'art1' : 'art0', image, {}); },
      setMask: function (image) { texture('mask', image, {}); gl.uniform4f(uniform.uMaskRect, ART.mask.x, ART.mask.y, ART.mask.size, 1); },
      // hand: the drawing's quads, drawn first. rest: every bead, link, the medal and the crucifix.
      setScene: function (hand, rest) {
        var data = new Float32Array((hand.length + rest.length) * 6 * STRIDE);
        var offset = 0;
        hand.forEach(function (item) { offset = fill(data, offset, item); });
        rest.forEach(function (item) { offset = fill(data, offset, item); });
        gl.bindBuffer(gl.ARRAY_BUFFER, fixed);
        gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
        counts.hand = hand.length * 6;
        counts.rest = rest.length * 6;
      },
      resize: function (width, height, ratio) {
        var w = Math.max(1, Math.round(width * ratio));
        var h = Math.max(1, Math.round(height * ratio));
        if (canvas.width !== w) canvas.width = w;
        if (canvas.height !== h) canvas.height = h;
        gl.viewport(0, 0, w, h);
        gl.uniform2f(uniform.uView, width, height);
      },
      draw: function (frame) {
        gl.uniform2f(uniform.uCam, frame.x, frame.y);
        gl.uniform1f(uniform.uZoom, frame.zoom);
        gl.uniform1f(uniform.uRot, frame.rot);
        gl.uniform2f(uniform.uFocus, frame.focusX, frame.focusY);
        gl.uniform4f(uniform.uFade, frame.soft0, frame.soft1, frame.gone0, frame.gone1);
        gl.uniform3f(uniform.uTint, frame.tintR, frame.tintG, frame.tintB);
        gl.uniform1f(uniform.uLit, frame.lit);
        gl.uniform1f(uniform.uArtSlot, frame.artSlot);
        gl.uniform4f(uniform.uHandFx, frame.handBlur, frame.handLight, frame.handAlpha, ready.tile ? frame.tile : 0);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        if (ready.hand && counts.hand) { point(fixed); gl.drawArrays(gl.TRIANGLES, 0, counts.hand); }
        drawMoving(frame.under);
        point(fixed);
        gl.drawArrays(gl.TRIANGLES, counts.hand, counts.rest);
        drawMoving(frame.over);
      }
    };
  }

  /* The rosary as a fixed layout in "drawing units": the hand drawing is the
     square from (0,0) to (1,1) and its three beads are the three Hail Marys of
     the pendant. The crucifix hangs below them; the loop of five decades lies
     above, joined at the medal. 59 beads in all. */
  function buildWorld() {
    var b = ART.beads;
    var r = b[1].r;
    var down = { x: b[2].x - b[0].x, y: b[2].y - b[0].y };
    var length = Math.sqrt(down.x * down.x + down.y * down.y);
    down.x /= length; down.y /= length;
    function along(origin, t) { return { x: origin.x + down.x * t, y: origin.y + down.y * t }; }

    var beads = [];
    var links = [];
    var extras = [];
    function chain(from, fromR, to, toR) {
      var dx = to.x - from.x;
      var dy = to.y - from.y;
      var dist = Math.sqrt(dx * dx + dy * dy);
      var gap = dist - fromR - toR;
      if (gap <= 0) return;
      var ux = dx / dist;
      var uy = dy / dist;
      var n = Math.max(1, Math.round(gap / (r * 0.6)));
      var step = gap / n;
      var rot = Math.atan2(-ux, uy);
      for (var i = 0; i < n; i++) {
        var t = fromR + step * (i + 0.5);
        links.push({ x: from.x + ux * t, y: from.y + uy * t, hw: r * 0.27, hh: step * 0.74, rot: rot, kind: i % 2 ? 2 : 1 });
      }
    }

    var big = r * 1.25;
    var medalR = r * 1.9;
    var top = b[0];
    var bottom = b[2];
    var ofLow = along(bottom, r * 4.5);
    var crossTop = along(ofLow, big + r * 1.9);
    var cross = along(ofLow, big + r * 1.9 + r * 4.95);
    var ofHigh = along(top, -r * 4.5);
    var medal = along(ofHigh, -r * 6.6);
    var upright = Math.atan2(-down.x, down.y);

    extras.push({ x: cross.x, y: cross.y, hw: r * 2.65, hh: r * 5.05, rot: upright, kind: 7 });
    chain(ofLow, big, crossTop, 0);
    beads.push({ x: ofLow.x, y: ofLow.y, r: big, kind: 4 });
    chain(bottom, bottom.r, ofLow, big);
    beads.push({ x: bottom.x, y: bottom.y, r: bottom.r * 1.015, kind: 0 });
    // A touch larger than the drawn bead it sits on, so no blue of the drawing shows round it once it has turned gold.
    beads.push({ x: b[1].x, y: b[1].y, r: b[1].r * 1.025, kind: 0, pinched: true });
    beads.push({ x: top.x, y: top.y, r: top.r * 1.015, kind: 0 });
    chain(top, top.r, ofHigh, big);
    beads.push({ x: ofHigh.x, y: ofHigh.y, r: big, kind: 4 });
    chain(ofHigh, big, medal, medalR);
    extras.push({ x: medal.x, y: medal.y, hw: medalR, hh: r * 2.2, rot: upright, kind: 5 });

    // The loop: five decades with an Our Father bead between each.
    var spacing = r * 2.55;
    var toMedal = r * 5.2;
    var toBig = r * 4.5;
    var marks = [];
    var bigAt = [];
    var decadeMid = 0;
    var at = toMedal;
    for (var decade = 0; decade < 5; decade++) {
      for (var i = 0; i < 10; i++) {
        marks.push({ t: at, r: r, kind: 0 });
        if (decade === 0 && i === 4) decadeMid = at + spacing / 2;
        if (i < 9) at += spacing;
      }
      if (decade < 4) { at += toBig; marks.push({ t: at, r: big, kind: 4 }); bigAt.push(at); at += toBig; }
    }
    var total = at + toMedal;
    var base = Math.atan2(down.y, down.x);
    /* The loop lies the way a rosary does when it is put down: not a drawn
       circle but a soft teardrop that comes to a point at the medal and
       wanders a little on the way round. shape() is its radius at each angle
       from the medal; the beads are then spaced along it by real distance. */
    function shape(theta) {
      var toMedalSide = Math.sin(theta / 2) / 0.34;
      return 1 + 0.26 * Math.exp(-toMedalSide * toMedalSide) + 0.085 * Math.cos(2 * theta + 0.7) + 0.05 * Math.sin(3 * theta + 1.2) + 0.028 * Math.cos(5 * theta + 0.4);
    }
    var SAMPLES = 720;
    var xs = [], ys = [], lengths = [0], turns = [], n, theta, radius, from, to, turn;
    for (n = 0; n <= SAMPLES; n++) {
      theta = (n / SAMPLES) * Math.PI * 2;
      radius = shape(theta);
      xs.push(Math.cos(base + theta) * radius);
      ys.push(Math.sin(base + theta) * radius);
      if (n) lengths.push(lengths[n - 1] + Math.sqrt((xs[n] - xs[n - 1]) * (xs[n] - xs[n - 1]) + (ys[n] - ys[n - 1]) * (ys[n] - ys[n - 1])));
    }
    // The direction of travel at each sample, kept continuous so the camera can turn with it.
    for (n = 0; n <= SAMPLES; n++) {
      from = Math.max(0, n - 1); to = Math.min(SAMPLES, n + 1);
      turn = Math.atan2(ys[to] - ys[from], xs[to] - xs[from]);
      if (n) { while (turn - turns[n - 1] > Math.PI) turn -= Math.PI * 2; while (turn - turns[n - 1] < -Math.PI) turn += Math.PI * 2; }
      turns.push(turn);
    }
    var R = total / lengths[SAMPLES];
    var centre = { x: medal.x - xs[0] * R, y: medal.y - ys[0] * R };
    function onLoop(t) {
      var want = Math.max(0, Math.min(1, t / total)) * lengths[SAMPLES];
      var low = 0, high = SAMPLES, middle, part;
      while (high - low > 1) { middle = (low + high) >> 1; if (lengths[middle] <= want) low = middle; else high = middle; }
      part = (want - lengths[low]) / ((lengths[high] - lengths[low]) || 1);
      return {
        x: centre.x + (xs[low] + (xs[high] - xs[low]) * part) * R,
        y: centre.y + (ys[low] + (ys[high] - ys[low]) * part) * R,
        angle: turns[low] + (turns[high] - turns[low]) * part - Math.PI / 2
      };
    }
    var previous = { x: medal.x, y: medal.y, r: medalR };
    marks.forEach(function (mark) {
      var p = onLoop(mark.t);
      chain(previous, previous.r, p, mark.r);
      beads.push({ x: p.x, y: p.y, r: mark.r, kind: mark.kind });
      previous = { x: p.x, y: p.y, r: mark.r };
    });
    chain(previous, previous.r, medal, medalR);

    /* Where each decade's mystery is announced. The first is the pendant's
       upper Our Father bead, seen the way the hand holds it; the other four
       are the Our Father beads round the loop. "angle" is the turn that
       stands the strand upright on screen there, and "t" the distance round
       the loop, which lets the camera follow the strand between them. */
    var stations = [{ x: ofHigh.x, y: ofHigh.y, angle: base - Math.PI / 2, t: null }];
    bigAt.forEach(function (t) { var p = onLoop(t); stations.push({ x: p.x, y: p.y, angle: p.angle, t: t }); });
    var mid = onLoop(decadeMid);

    var x0 = 0.08, x1 = -1e9, y0 = 1e9, y1 = 0.98;
    beads.forEach(function (bead) { x0 = Math.min(x0, bead.x - bead.r); x1 = Math.max(x1, bead.x + bead.r); y0 = Math.min(y0, bead.y - bead.r); y1 = Math.max(y1, bead.y + bead.r); });
    x1 = Math.max(x1, cross.x + r * 2.65);
    y1 = Math.max(y1, cross.y + r * 5.05);

    var rest = links.concat(extras);
    beads.forEach(function (bead) { rest.push({ x: bead.x, y: bead.y, hw: bead.r * 1.06, hh: bead.r * 1.06, kind: bead.kind, p: [0, 0, bead.pinched ? 1 : 0, 0] }); });
    var half = ART.tile.size / 2;

    return {
      r: r,
      down: down,
      pinch: { x: b[1].x, y: b[1].y },
      bounds: { x0: x0, x1: x1, y0: y0, y1: y1 },
      stations: stations,
      decade: { x: mid.x, y: mid.y, angle: mid.angle, t: decadeMid },
      onLoop: onLoop,
      hand: [
        { x: 0.5, y: 0.5, hw: 0.5, hh: 0.5, kind: 9 },
        { x: ART.tile.x + half, y: ART.tile.y + half, hw: half, hh: half, kind: 10 }
      ],
      rest: rest
    };
  }

  window.OneVineRosaryGL = { createRenderer: createRenderer, buildWorld: buildWorld };
})();
